import { useEffect, useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { SectionHead, Reveal } from "@/components/Reveal";
import MediaCard from "@/components/MediaCard";
import MediaFrame from "@/components/MediaFrame";
import { api } from "@/lib/api";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "photo", label: "Photos" },
  { key: "video", label: "Videos" },
];

const Events = () => {
  const [posts, setPosts] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api
      .get("/media")
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]));
  }, []);

  const filtered = (posts || []).filter((p) => filter === "all" || p.media_type === filter);

  return (
    <main className="pt-32 pb-24 bg-gradient-to-b from-lavender-100 via-lavender-50 to-white min-h-screen" data-testid="events-page-bg">
      <section className="max-w-7xl mx-auto px-6" data-testid="events-page">
        <SectionHead
          eyebrow="Events & Media"
          title="Moments from our community"
          sub="Photos and video clips from real Y.E.S. events — with dates and captions, added by our team."
        />

        <Reveal delay={0.1} className="mt-10">
          <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="Filter media">
            {FILTERS.map((f) => {
              const count = (posts || []).filter((p) => f.key === "all" || p.media_type === f.key).length;
              return (
                <button
                  key={f.key}
                  role="tab"
                  aria-selected={filter === f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`media-filter-${f.key}`}
                  className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
                    filter === f.key
                      ? "bg-purple-700 text-white shadow-lg shadow-purple-300/60"
                      : "bg-white border border-purple-200 text-purple-900 hover:border-purple-500"
                  }`}
                >
                  {f.label} <span className="font-mono text-xs opacity-70">{posts ? count : "…"}</span>
                </button>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="mt-8">
          <div className="flex items-start gap-3 rounded-2xl border border-purple-200 bg-lavender-100 px-5 py-4">
            <ShieldCheck className="w-5 h-5 text-purple-700 shrink-0 mt-0.5" />
            <p className="text-sm text-purple-950 leading-relaxed">
              Every photo and video is reviewed by our team and published only with the appropriate
              media permissions on file. Unpublished uploads stay private.
            </p>
          </div>
        </Reveal>

        {posts === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12" data-testid="media-loading">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-video rounded-2xl bg-purple-100 animate-pulse flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
              </div>
            ))}
          </div>
        ) : filtered.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12" data-testid="media-gallery">
            {filtered.map((p, i) => (
              <MediaCard key={p.id} post={p} index={i} />
            ))}
          </div>
        ) : (
          <div className="mt-12" data-testid="media-empty">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <MediaFrame label="Photo Upload Slot" sub="Event photos will appear here once uploaded and approved." />
              <MediaFrame label="Video Upload Slot" sub="Event video clips will appear here once uploaded and approved." />
              <MediaFrame label="Highlight Upload Slot" sub="Season highlights will appear here once uploaded and approved." />
            </div>
            <p className="text-center text-slate-500 text-sm mt-8">
              Nothing published yet — the first event highlights are on their way.
            </p>
          </div>
        )}
      </section>
    </main>
  );
};

export default Events;